import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { MarkerClusterer, Renderer, Cluster, ClusterStats } from '@googlemaps/markerclusterer';
import { useAuth } from '../hooks/useAuth';
import { useGeolocation } from '../hooks/useGeolocation';
import { Projects, Scenes } from 'trace-viewer/cms';
import type { ProjectResponseDto, SceneAccessorResponseDto, SceneResponseDto } from 'trace-viewer/cms';
import { apiConfig } from '../auth';
import { resolveUrl } from '../utils/url';
import { haversineKm } from '../utils/haversine';
import { buildShareUrl, downloadKml } from '../utils/export';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

/**
 * A Map ID is required for AdvancedMarkerElement.
 * Supply VITE_GOOGLE_MAPS_MAP_ID in your deployment env.
 * In development you may use the built-in Google placeholder "DEMO_MAP_ID".
 */
const GOOGLE_MAPS_MAP_ID = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined) ?? 'DEMO_MAP_ID';

// Warn in production if DEMO_MAP_ID is being used — AdvancedMarkerElement may silently degrade.
if (import.meta.env.PROD && !import.meta.env.VITE_GOOGLE_MAPS_MAP_ID) {
    console.warn(
        '[MapView] VITE_GOOGLE_MAPS_MAP_ID is not set; falling back to DEMO_MAP_ID. ' +
        'AdvancedMarkerElement may not render correctly in production.',
    );
}

/** Default location (London) used when geolocation is unavailable. */
const DEFAULT_LAT = 51.5074;
const DEFAULT_LNG = -0.1278;

/** Distance in km that triggers a re-fetch when the map center moves. */
const RECENTER_THRESHOLD = 2.0;

/**
 * Search radius in **metres** passed to the projects API as `distance`.
 * The API caps this at 5000 m.
 */
const SEARCH_RADIUS_M = 5000;

const MOBILE_QUERY = '(max-width: 480px)';

/** Sentinel ID used to represent the "Independent Scenes" virtual project. */
const INDEPENDENT_SCENES_ID = '__independent_scenes__';

/**
 * Pick the scene accessor nearest to a reference point.
 * Falls back to the first accessor if none have coords or no reference is given.
 */
function nearestAccessor(
    accessors: SceneAccessorResponseDto[] | null | undefined,
    ref?: { lat: number; lng: number } | null,
): SceneAccessorResponseDto | undefined {
    if (!accessors?.length) return undefined;
    if (!ref) return accessors[0];
    let best = accessors[0];
    let bestDist = Infinity;
    for (const acc of accessors) {
        if (acc.latitude == null || acc.longitude == null) continue;
        const dist = haversineKm(ref.lat, ref.lng, acc.latitude, acc.longitude);
        if (dist < bestDist) {
            bestDist = dist;
            best = acc;
        }
    }
    return best;
}

function createProjectMarkerContent(logoUrl?: string | null): HTMLElement {
    const resolved = resolveUrl(logoUrl);
    if (resolved) {
        // Custom image marker — circular with border, no PinElement needed
        const img = document.createElement('img');
        img.src = resolved;
        img.style.cssText =
            'width:36px;height:36px;border-radius:50%;object-fit:cover;' +
            'border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);' +
            'display:block;';
        return img;
    }

    // No logo — use a plain PinElement (no deprecated properties)
    return new google.maps.marker.PinElement({
        background: '#7C3AED',
        borderColor: '#fff',
        glyphColor: '#fff',
        scale: 1.0,
    });
}

function createSceneMarkerContent(imageUrl?: string | null): HTMLElement {
    // Note: PinElement extends HTMLElement so the union return is compatible.
    const resolved = resolveUrl(imageUrl);
    if (resolved) {
        const img = document.createElement('img');
        img.src = resolved;
        img.style.cssText =
            'width:28px;height:28px;border-radius:6px;object-fit:cover;' +
            'border:2px solid #4cd6c0;box-shadow:0 2px 6px rgba(0,0,0,0.3);' +
            'display:block;';
        return img;
    }
    return new google.maps.marker.PinElement({
        background: '#4cd6c0',
        borderColor: '#fff',
        glyphColor: '#fff',
        scale: 0.8,
    });
}

function createUserLocationContent(): HTMLDivElement {
    const outer = document.createElement('div');
    outer.style.cssText = 'width:16px;height:16px;border-radius:50%;background:#4285F4;border:2px solid #fff;box-shadow:0 0 0 2px rgba(66,133,244,0.3),0 2px 4px rgba(0,0,0,0.3);';
    return outer;
}

// ---------------------------------------------------------------------------
// Custom cluster renderer — uses AdvancedMarkerElement to avoid the deprecated
// google.maps.Marker class used by the library's DefaultRenderer.
// ---------------------------------------------------------------------------
class AdvancedClusterRenderer implements Renderer {
    render(
        { count, position }: Cluster,
        _stats: ClusterStats,
        _map: google.maps.Map,
    ): google.maps.marker.AdvancedMarkerElement {
        const pin = new google.maps.marker.PinElement({
            background: '#7C3AED',
            borderColor: '#fff',
            glyphColor: '#fff',
            scale: 1.3,
            glyph: String(count),
        });

        return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: pin,
            zIndex: 1000 + count,
        });
    }
}

const advancedClusterRenderer = new AdvancedClusterRenderer();

// ---------------------------------------------------------------------------
// Inner map component (rendered by Wrapper once Maps JS is loaded)
// ---------------------------------------------------------------------------
export interface MapInnerHandle {
    panTo: (lat: number, lng: number, minZoom?: number) => void;
}

interface MapInnerProps {
    userLat: number;
    userLng: number;
    projects: ProjectResponseDto[];
    sceneMarkers: SceneResponseDto[];
    /** Reference location for picking the nearest scene accessor (e.g. expanded project location). */
    sceneMarkerRef?: { lat: number; lng: number } | null;
    onSelectProject: (project: ProjectResponseDto) => void;
    onSelectSceneMarker: (scene: SceneResponseDto) => void;
    onCenterChanged: (lat: number, lng: number) => void;
    searchCenter: { lat: number; lng: number } | null;
    /** When true, hide the search radius circle and user marker (curated mode). */
    hideGeoOverlays?: boolean;
}

const MapInner = React.forwardRef<MapInnerHandle, MapInnerProps>(function MapInner({ userLat, userLng, projects, sceneMarkers, sceneMarkerRef: sceneRefLocation, onSelectProject, onSelectSceneMarker, onCenterChanged, searchCenter, hideGeoOverlays }, ref) {
    const mapDivRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const circleRef = useRef<google.maps.Circle | null>(null);
    const userMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
    const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
    /** Parallel array of gmpclick listener functions, indexed with markersRef. */
    const markerListenersRef = useRef<Array<() => void>>([]);
    /**
     * Cache of project markers keyed by project id, so we can reuse markers across
     * project-list updates instead of tearing them all down (which caused a visible flash).
     * Stores the AdvancedMarkerElement, its current click listener, and the last-applied
     * signature (logoUrl|graphicUrl|lat|lng|name) so we only mutate the DOM when something
     * actually changed.
     */
    const markerCacheRef = useRef<Map<string, {
        marker: google.maps.marker.AdvancedMarkerElement;
        listener: () => void;
        signature: string;
    }>>(new Map());
    const sceneMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
    const sceneMarkerListenersRef = useRef<Array<() => void>>([]);
    const sceneMarkerCacheRef = useRef<Map<string, {
        marker: google.maps.marker.AdvancedMarkerElement;
        listener: () => void;
        signature: string;
    }>>(new Map());
    const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const zoomListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const initialFitDoneRef = useRef(false);

    useImperativeHandle(ref, () => ({
        panTo(lat: number, lng: number, minZoom?: number) {
            if (!mapRef.current) return;
            mapRef.current.panTo({ lat, lng });
            if (minZoom != null && (mapRef.current.getZoom() ?? 0) < minZoom) {
                mapRef.current.setZoom(minZoom);
            }
        },
    }), []);

    // Keep a stable ref to onCenterChanged to avoid stale closure in the idle listener.
    const onCenterChangedRef = useRef(onCenterChanged);
    useEffect(() => {
        onCenterChangedRef.current = onCenterChanged;
    }, [onCenterChanged]);

    // Initialise map once.
    // userLat/userLng are intentionally excluded from deps: we only use them as the initial
    // center; subsequent position changes are handled by the update effect below.
    useEffect(() => {
        if (!mapDivRef.current || mapRef.current) return;

        const map = new google.maps.Map(mapDivRef.current, {
            center: { lat: userLat, lng: userLng },
            zoom: 13,
            mapId: GOOGLE_MAPS_MAP_ID,
            colorScheme: google.maps.ColorScheme.DARK,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: 'greedy',
            clickableIcons: false,
        });
        mapRef.current = map;

        // User location marker and search radius circle (hidden in curated mode)
        if (!hideGeoOverlays) {
            userMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
                position: { lat: userLat, lng: userLng },
                map,
                content: createUserLocationContent(),
                title: 'Your location',
                zIndex: 100,
            });

            circleRef.current = new google.maps.Circle({
                map,
                center: { lat: userLat, lng: userLng },
                radius: SEARCH_RADIUS_M,
                fillColor: '#7C3AED',
                fillOpacity: 0.06,
                strokeColor: '#7C3AED',
                strokeOpacity: 0.4,
                strokeWeight: 1.5,
            });

            // Hide the search radius circle when zoomed in enough that the edge isn't visible
            const CIRCLE_HIDE_ZOOM = 14;
            zoomListenerRef.current = map.addListener('zoom_changed', () => {
                const zoom = map.getZoom() ?? 0;
                circleRef.current?.setVisible(zoom < CIRCLE_HIDE_ZOOM);
            });
        }

        // "My location" custom control — placed below the zoom buttons
        if (!hideGeoOverlays) {
            const btn = document.createElement('button');
            btn.title = 'Go to my location';
            btn.setAttribute('aria-label', 'Go to my location');
            btn.style.cssText =
                'background:#2b2b2b;border:none;border-radius:2px;box-shadow:0 1px 4px rgba(0,0,0,0.3);' +
                'cursor:pointer;margin:0 10px 10px 0;padding:0;width:40px;height:40px;' +
                'display:flex;align-items:center;justify-content:center;';
            btn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#e8e8e8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/></svg>';
            btn.addEventListener('click', () => {
                map.panTo({ lat: userLat, lng: userLng });
                const zoom = map.getZoom() ?? 0;
                if (zoom < 14) map.setZoom(14);
            });
            map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(btn);
        }

        // Marker clusterer — custom renderer avoids deprecated google.maps.Marker
        clustererRef.current = new MarkerClusterer({
            map,
            markers: [],
            renderer: advancedClusterRenderer,
            onClusterClick: (_event, cluster, _map) => {
                const bounds = cluster.bounds;
                if (bounds) map.fitBounds(bounds, 80);
            },
        });

        // Idle listener: call through ref to always use the latest callback
        idleListenerRef.current = map.addListener('idle', () => {
            const c = map.getCenter();
            if (c) onCenterChangedRef.current(c.lat(), c.lng());
        });

        return () => {
            // Remove event listeners
            idleListenerRef.current?.remove();
            idleListenerRef.current = null;
            zoomListenerRef.current?.remove();
            zoomListenerRef.current = null;

            // Detach project markers from the map and clean up listeners
            markersRef.current.forEach((marker, i) => {
                const listener = markerListenersRef.current[i];
                if (listener) marker.removeEventListener('gmp-click', listener);
                marker.map = null;
            });
            markerListenersRef.current = [];
            clustererRef.current?.clearMarkers(true);
            clustererRef.current = null;
            markersRef.current = [];
            markerCacheRef.current.clear();

            // Clear scene markers
            sceneMarkersRef.current.forEach((marker, i) => {
                const listener = sceneMarkerListenersRef.current[i];
                if (listener) marker.removeEventListener('gmp-click', listener);
                marker.map = null;
            });
            sceneMarkersRef.current = [];
            sceneMarkerListenersRef.current = [];
            sceneMarkerCacheRef.current.clear();

            // Remove search radius circle
            circleRef.current?.setMap(null);
            circleRef.current = null;

            // Remove user location marker
            if (userMarkerRef.current) {
                userMarkerRef.current.map = null;
                userMarkerRef.current = null;
            }

            mapRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update user marker position when userLat/userLng props change after mount
    useEffect(() => {
        if (!userMarkerRef.current) return;
        userMarkerRef.current.position = { lat: userLat, lng: userLng };
    }, [userLat, userLng]);

    // Move the search radius circle to follow the current search center
    useEffect(() => {
        if (!circleRef.current || !searchCenter) return;
        circleRef.current.setCenter(searchCenter);
    }, [searchCenter]);

    // Update project markers when projects list changes.
    //
    // Diffs by project id rather than tearing down and recreating every marker: when the user
    // drags past the re-fetch threshold we'd otherwise see every icon flash off/on twice (once
    // when fetchLoading toggled a re-render, once when the new data arrived). Now we reuse
    // existing markers whose id is still present and only mutate their position/content/title
    // when the underlying signature actually changes.
    useEffect(() => {
        if (!mapRef.current || !clustererRef.current) return;
        const cache = markerCacheRef.current;

        // Build the set of project ids that should remain on the map this pass.
        const nextIds = new Set<string>();
        const newMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
        const newListeners: Array<() => void> = [];
        const addedToClusterer: google.maps.marker.AdvancedMarkerElement[] = [];

        for (const project of projects) {
            const acc = project.projectAccessors?.[0];
            if (acc?.latitude == null || acc?.longitude == null) continue;
            // Projects without a stable id can't be diffed — skip (should never happen in practice).
            if (project.id == null) continue;
            nextIds.add(project.id);

            const lat = acc.latitude;
            const lng = acc.longitude;
            const title = project.name ?? 'Project';
            const iconUrl = project.logoUrl ?? project.graphicUrl ?? '';
            const signature = `${iconUrl}|${lat}|${lng}|${title}`;

            const existing = cache.get(project.id);
            if (existing) {
                // Reuse the marker. Only touch the DOM if something actually changed.
                if (existing.signature !== signature) {
                    existing.marker.position = { lat, lng };
                    existing.marker.title = title;
                    existing.marker.content = createProjectMarkerContent(iconUrl || null);
                    existing.signature = signature;
                }
                // Rewire the click listener to capture the latest project reference.
                existing.marker.removeEventListener('gmp-click', existing.listener);
                const listener = () => onSelectProject(project);
                existing.marker.addEventListener('gmp-click', listener);
                existing.listener = listener;
                newMarkers.push(existing.marker);
                newListeners.push(listener);
            } else {
                const marker = new google.maps.marker.AdvancedMarkerElement({
                    position: { lat, lng },
                    map: mapRef.current,
                    content: createProjectMarkerContent(iconUrl || null),
                    title,
                    gmpClickable: true,
                });
                const listener = () => onSelectProject(project);
                marker.addEventListener('gmp-click', listener);
                cache.set(project.id, { marker, listener, signature });
                newMarkers.push(marker);
                newListeners.push(listener);
                addedToClusterer.push(marker);
            }
        }

        // Remove stale markers whose project is no longer in the list.
        const staleRemoved: google.maps.marker.AdvancedMarkerElement[] = [];
        for (const [id, entry] of cache) {
            if (!nextIds.has(id)) {
                entry.marker.removeEventListener('gmp-click', entry.listener);
                entry.marker.map = null;
                staleRemoved.push(entry.marker);
                cache.delete(id);
            }
        }

        markersRef.current = newMarkers;
        markerListenersRef.current = newListeners;
        // Incrementally update the clusterer instead of clearMarkers()/addMarkers() —
        // a full replace re-runs clustering and can visibly flicker.
        if (staleRemoved.length > 0) {
            clustererRef.current.removeMarkers(staleRemoved, true);
        }
        if (addedToClusterer.length > 0) {
            clustererRef.current.addMarkers(addedToClusterer);
        } else if (staleRemoved.length > 0) {
            // removeMarkers was called with noDraw=true; trigger a redraw.
            clustererRef.current.render();
        }

        // In curated mode, fit map bounds on initial load only
        if (hideGeoOverlays && !initialFitDoneRef.current && newMarkers.length > 0 && mapRef.current) {
            initialFitDoneRef.current = true;
            const bounds = new google.maps.LatLngBounds();
            newMarkers.forEach(m => {
                if (m.position) bounds.extend(m.position as google.maps.LatLngLiteral);
            });
            if (newMarkers.length === 1) {
                mapRef.current.setCenter(bounds.getCenter());
                mapRef.current.setZoom(15);
            } else {
                mapRef.current.fitBounds(bounds, 60);
            }
        }
    }, [projects, onSelectProject, hideGeoOverlays]);

    // Update scene markers when sceneMarkers prop changes. Diffs by scene id to avoid
    // tearing down markers that are still valid — same reasoning as the project marker effect.
    useEffect(() => {
        if (!mapRef.current) return;
        const cache = sceneMarkerCacheRef.current;

        const resolved = sceneMarkers
            .map(scene => ({ scene, acc: nearestAccessor(scene.sceneAccessors, sceneRefLocation) }))
            .filter((entry): entry is { scene: SceneResponseDto; acc: SceneAccessorResponseDto } =>
                entry.acc?.latitude != null && entry.acc?.longitude != null);

        const nextIds = new Set<string>();
        const newMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
        const newListeners: Array<() => void> = [];

        for (const { scene, acc } of resolved) {
            if (scene.id == null) continue;
            nextIds.add(scene.id);

            const lat = acc.latitude as number;
            const lng = acc.longitude as number;
            const title = scene.name ?? 'Scene';
            const iconUrl = scene.imageUrl ?? '';
            const signature = `${iconUrl}|${lat}|${lng}|${title}`;

            const existing = cache.get(scene.id);
            if (existing) {
                if (existing.signature !== signature) {
                    existing.marker.position = { lat, lng };
                    existing.marker.title = title;
                    existing.marker.content = createSceneMarkerContent(iconUrl || null);
                    existing.signature = signature;
                }
                existing.marker.removeEventListener('gmp-click', existing.listener);
                const listener = () => onSelectSceneMarker(scene);
                existing.marker.addEventListener('gmp-click', listener);
                existing.listener = listener;
                // In case it was detached (stale-removal on a prior pass), reattach to the map.
                if (existing.marker.map !== mapRef.current) {
                    existing.marker.map = mapRef.current;
                }
                newMarkers.push(existing.marker);
                newListeners.push(listener);
            } else {
                const marker = new google.maps.marker.AdvancedMarkerElement({
                    position: { lat, lng },
                    map: mapRef.current,
                    content: createSceneMarkerContent(iconUrl || null),
                    title,
                    gmpClickable: true,
                    zIndex: 50,
                });
                const listener = () => onSelectSceneMarker(scene);
                marker.addEventListener('gmp-click', listener);
                cache.set(scene.id, { marker, listener, signature });
                newMarkers.push(marker);
                newListeners.push(listener);
            }
        }

        // Detach scene markers whose scene is no longer displayed.
        for (const [id, entry] of cache) {
            if (!nextIds.has(id)) {
                entry.marker.removeEventListener('gmp-click', entry.listener);
                entry.marker.map = null;
                cache.delete(id);
            }
        }

        sceneMarkersRef.current = newMarkers;
        sceneMarkerListenersRef.current = newListeners;
    }, [sceneMarkers, sceneRefLocation, onSelectSceneMarker]);

    return <div ref={mapDivRef} className="map-inner" style={{ width: '100%', height: '100%' }} />;
});

// ---------------------------------------------------------------------------
// Loading / error render for Wrapper
// ---------------------------------------------------------------------------
function MapStatus({ status }: { status: Status }) {
    if (status === Status.LOADING) {
        return (
            <div className="map-status">
                <div className="loading-spinner" />
                <span>Loading map…</span>
            </div>
        );
    }
    return (
        <div className="map-status map-status--error">
            Map failed to load. Check your API key configuration.
        </div>
    );
}

// ---------------------------------------------------------------------------
// Public MapView component
// ---------------------------------------------------------------------------
interface MapViewProps {
    /** Called when a project is tapped — implementations should open scenes. */
    onSelectProject: (project: ProjectResponseDto, scenes: SceneResponseDto[]) => void;
    /** Called when a scene is tapped in the side panel — navigates to landing page. */
    onSelectScene: (scene: SceneResponseDto) => void;
    /** IDs of projects in the user's nearby list (Experience tab). */
    nearbyProjectIds: Set<string>;
    /** When non-null, show only these curated projects instead of geo-fetched ones. */
    curatedProjects?: ProjectResponseDto[] | null;
}

export function MapView({ onSelectProject, onSelectScene, nearbyProjectIds, curatedProjects }: MapViewProps) {
    const { token } = useAuth();

    const { latitude: userLat, longitude: userLng, error: geoError, loading: geoLoading } = useGeolocation();

    const isCurated = curatedProjects != null;
    const [fetchedProjects, setFetchedProjects] = useState<ProjectResponseDto[]>([]);
    const [independentScenes, setIndependentScenes] = useState<SceneResponseDto[]>([]);
    const projects = isCurated ? curatedProjects : fetchedProjects;
    const [fetchLoading, setFetchLoading] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);

    // Selected project/scene shown in the popup card (null = no card visible)
    const [selectedProject, setSelectedProject] = useState<ProjectResponseDto | null>(null);
    const [selectedScene, setSelectedScene] = useState<SceneResponseDto | null>(null);

    // selectLoading: true while scenes are being fetched after a marker tap
    const [selectLoading, setSelectLoading] = useState(false);
    // selectError: non-null when the scene fetch failed (as opposed to genuinely empty)
    const [selectError, setSelectError] = useState<string | null>(null);

    // Track the center of the current search area (drives the circle overlay)
    const [searchCenter, setSearchCenter] = useState<{ lat: number; lng: number } | null>(null);
    // Track last fetch center to avoid redundant re-fetches
    const lastFetchCenter = useRef<{ lat: number; lng: number } | null>(null);

    // ---------------------------------------------------------------------------
    // Fetch nearby projects
    // ---------------------------------------------------------------------------
    const fetchProjects = useCallback(
        async (lat: number, lng: number) => {
            if (!token) return;
            setFetchLoading(true);
            setFetchError(null);
            const config = apiConfig(token);
            const geoQuery = { latitude: lat, longitude: lng, distance: SEARCH_RADIUS_M };
            // Decouple so a scenes failure doesn't break the primary projects load
            const [projectsResult, scenesResult] = await Promise.allSettled([
                new Projects(config).projectsList(geoQuery),
                new Scenes(config).scenesList(geoQuery),
            ]);
            if (projectsResult.status === 'fulfilled') {
                setFetchedProjects(projectsResult.value.data ?? []);
                lastFetchCenter.current = { lat, lng };
                setSearchCenter({ lat, lng });
                setPanelSearch(''); // clear stale filter from previous area
            } else {
                setFetchError('Could not refresh nearby projects. Check your connection.');
            }
            if (scenesResult.status === 'fulfilled') {
                setIndependentScenes((scenesResult.value.data ?? []).filter(s => !s.isAttached));
            } else {
                // Scenes failed — just hide the independent scenes row
                setIndependentScenes([]);
            }
            setFetchLoading(false);
        },
        [token],
    );

    // Initial fetch once we have location + token (skip in curated mode)
    useEffect(() => {
        if (isCurated) return;
        if (userLat == null || userLng == null || !token) return;
        if (lastFetchCenter.current) return; // already fetched
        fetchProjects(userLat, userLng);
    }, [userLat, userLng, token, fetchProjects, isCurated]);

    // Auto-dismiss transient fetch error toast — the underlying issue is usually
    // brief (flaky connection) and a sticky toast outlives its usefulness.
    useEffect(() => {
        if (!fetchError) return;
        const id = window.setTimeout(() => setFetchError(null), 5000);
        return () => window.clearTimeout(id);
    }, [fetchError]);

    // Ref to track panel expansion state without causing callback re-creation
    const panelExpandedIdRef = useRef<string | null>(null);

    // Re-fetch when map center moves beyond threshold (skip in curated mode or when a project is expanded)
    const handleCenterChanged = useCallback(
        (lat: number, lng: number) => {
            if (isCurated) return;
            if (panelExpandedIdRef.current) return; // don't re-sort while a project is focused
            const prev = lastFetchCenter.current;
            if (!prev) return;
            if (haversineKm(prev.lat, prev.lng, lat, lng) > RECENTER_THRESHOLD) {
                fetchProjects(lat, lng);
            }
        },
        [fetchProjects, isCurated],
    );

    // ---------------------------------------------------------------------------
    // Handle project marker tap → expand project (same as panel click)
    // ---------------------------------------------------------------------------
    // togglePanelProject is defined further down (it depends on state declared
    // below). To wire it into handleMarkerTap (which is captured by MapInner via
    // a stable callback), we route through this mutable ref. The actual
    // implementation is assigned in a useEffect below once togglePanelProject
    // exists in scope.
    const handleMarkerTapRef = useRef<(project: ProjectResponseDto) => void>(() => {});
    const handleMarkerTap = useCallback((project: ProjectResponseDto) => {
        handleMarkerTapRef.current(project);
    }, []);

    // Handle "Open" button on the popup card → load scenes then callback
    const selectingRef = useRef(false);

    const handleOpenProject = useCallback(
        async () => {
            if (!token || !selectedProject) return;
            if (selectingRef.current) return;
            selectingRef.current = true;
            setSelectLoading(true);
            setSelectError(null);
            try {
                let scenes: SceneResponseDto[] = selectedProject.scenes ?? [];
                if (scenes.length === 0 && selectedProject.id) {
                    let fetchFailed = false;
                    try {
                        const res = await new Projects(apiConfig(token)).scenesList(selectedProject.id);
                        scenes = res.data ?? [];
                    } catch {
                        fetchFailed = true;
                    }
                    if (fetchFailed) {
                        setSelectError('Could not load experiences for this project. Please try again.');
                        return;
                    }
                }
                onSelectProject(selectedProject, scenes);
                setSelectedProject(null);
            } finally {
                setSelectLoading(false);
                selectingRef.current = false;
            }
        },
        [token, selectedProject, onSelectProject],
    );

    // ---------------------------------------------------------------------------
    // Side panel — collapsible project/scene list
    // ---------------------------------------------------------------------------
    const [panelOpen, setPanelOpen] = useState(() => !window.matchMedia(MOBILE_QUERY).matches);
    const [panelExpandedId, _setPanelExpandedId] = useState<string | null>(null);
    const setPanelExpandedId = useCallback((id: string | null) => {
        _setPanelExpandedId(id);
        panelExpandedIdRef.current = id;
    }, []);
    const [panelScenes, setPanelScenes] = useState<SceneResponseDto[]>([]);
    const [panelScenesLoading, setPanelScenesLoading] = useState(false);
    const [panelScenesError, setPanelScenesError] = useState(false);
    const [panelSearch, setPanelSearch] = useState('');
    const mapInnerRef = useRef<MapInnerHandle>(null);

    // Project visibility selection — all selected by default
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() =>
        new Set(projects.map(p => p.id).filter(Boolean) as string[]),
    );
    // Track IDs the user explicitly unchecked so they stay off if they reappear
    const explicitlyHiddenRef = useRef<Set<string>>(new Set());

    // Keep selectedIds in sync when projects list changes: add new ones (unless explicitly hidden), prune stale ones.
    // The INDEPENDENT_SCENES_ID virtual project is preserved across syncs if selected (it's not in the projects list).
    useEffect(() => {
        const currentIds = new Set(projects.map(p => p.id).filter(Boolean) as string[]);
        setSelectedIds(prev => {
            const next = new Set<string>();
            for (const id of prev) {
                if (currentIds.has(id) || id === INDEPENDENT_SCENES_ID) next.add(id);
            }
            for (const id of currentIds) {
                if (!prev.has(id) && !explicitlyHiddenRef.current.has(id)) next.add(id);
            }
            if (next.size === prev.size && [...next].every(id => prev.has(id))) return prev;
            return next;
        });
    }, [projects]);

    const toggleProjectSelection = useCallback((id: string) => {
        // Dismiss any open popover when changing selection
        setSelectedProject(null);
        setSelectedScene(null);
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                explicitlyHiddenRef.current.add(id);
                // If unchecking the currently expanded project, collapse it
                if (panelExpandedIdRef.current === id) {
                    setPanelExpandedId(null);
                    setPanelScenes([]);
                }
            } else {
                next.add(id);
                explicitlyHiddenRef.current.delete(id);
            }
            return next;
        });
    }, [setPanelExpandedId]);

    const allSelected = useMemo(() => {
        const ids = projects.map(p => p.id).filter(Boolean) as string[];
        return ids.length > 0 && ids.every(id => selectedIds.has(id));
    }, [projects, selectedIds]);

    const toggleSelectAll = useCallback(() => {
        const ids = projects.map(p => p.id).filter(Boolean) as string[];
        if (allSelected) {
            setSelectedIds(new Set());
            for (const id of ids) explicitlyHiddenRef.current.add(id);
        } else {
            setSelectedIds(new Set(ids));
            explicitlyHiddenRef.current.clear();
        }
    }, [projects, allSelected]);

    const selectedProjects = useMemo(
        () => projects.filter(p => p.id && selectedIds.has(p.id)),
        [projects, selectedIds],
    );

    const handleCopyLink = useCallback(() => {
        if (selectedProjects.length === 0) return;
        const url = buildShareUrl(selectedProjects);
        void navigator.clipboard.writeText(url).then(() => {
            setShareCopied(true);
            setSharePopoverOpen(false);
            if (shareCopiedTimerRef.current) clearTimeout(shareCopiedTimerRef.current);
            shareCopiedTimerRef.current = setTimeout(() => setShareCopied(false), 2000);
        });
    }, [selectedProjects]);

    const handleExportKml = useCallback(() => {
        if (selectedProjects.length === 0) return;
        downloadKml(selectedProjects);
        setSharePopoverOpen(false);
    }, [selectedProjects]);

    const sharePopoverRef = useRef<HTMLDivElement>(null);

    // Auto-collapse/expand panel on screen width changes + track mobile state
    const isMobileRef = useRef(window.matchMedia(MOBILE_QUERY).matches);
    useEffect(() => {
        const mql = window.matchMedia(MOBILE_QUERY);
        const handler = (e: MediaQueryListEvent) => {
            isMobileRef.current = e.matches;
            setPanelOpen(!e.matches);
        };
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    const handleCollapseProject = useCallback(() => {
        setPanelExpandedId(null);
        setPanelScenes([]);
        setSelectedProject(null);
        setSelectedScene(null);
    }, [setPanelExpandedId]);

    /** Ensure a project ID is checked (clears any explicit-hidden state). */
    const ensureSelected = useCallback((id: string) => {
        setSelectedIds(prev => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
        explicitlyHiddenRef.current.delete(id);
    }, []);

    const togglePanelProject = useCallback(async (project: ProjectResponseDto) => {
        if (!project.id) return;
        const id = project.id;
        if (panelExpandedId === id) {
            handleCollapseProject();
            return;
        }

        // Auto-check the project when expanding it (so its scenes appear on the map)
        ensureSelected(id);

        // Show project popover and center map
        setSelectedProject(project);
        setSelectedScene(null);
        const acc = project.projectAccessors?.[0];
        if (acc?.latitude != null && acc?.longitude != null) {
            mapInnerRef.current?.panTo(acc.latitude, acc.longitude, 15);
        }

        // Expand — use inline scenes if available
        if (project.scenes?.length) {
            setPanelExpandedId(id);
            setPanelScenes(project.scenes);
            return;
        }

        // Fetch scenes
        if (!token) return;
        setPanelExpandedId(id);
        setPanelScenes([]);
        setPanelScenesError(false);
        setPanelScenesLoading(true);
        try {
            const res = await new Projects(apiConfig(token)).scenesList(id);
            if (panelExpandedIdRef.current !== id) return; // user switched away
            setPanelScenes(res.data ?? []);
        } catch {
            if (panelExpandedIdRef.current === id) setPanelScenesError(true);
        } finally {
            setPanelScenesLoading(false);
        }
    }, [panelExpandedId, token, handleCollapseProject, setPanelExpandedId, ensureSelected]);

    // Wire marker tap to toggle the same as panel click
    useEffect(() => {
        handleMarkerTapRef.current = (project: ProjectResponseDto) => {
            void togglePanelProject(project);
        };
    }, [togglePanelProject]);

    // Retry fetching scenes for the currently expanded project
    const retryFetchScenes = useCallback(async (project: ProjectResponseDto) => {
        if (!project.id || !token) return;
        const id = project.id;
        setPanelScenesError(false);
        setPanelScenesLoading(true);
        try {
            const res = await new Projects(apiConfig(token)).scenesList(id);
            if (panelExpandedIdRef.current !== id) return;
            setPanelScenes(res.data ?? []);
        } catch {
            if (panelExpandedIdRef.current === id) setPanelScenesError(true);
        } finally {
            setPanelScenesLoading(false);
        }
    }, [token]);

    // When a project is expanded, hide its marker — only scene markers are shown
    // Filter map markers: hide unselected projects, and hide the expanded project (its scenes show instead)
    // Memoized so the array identity is stable across unrelated re-renders (e.g. fetchLoading toggling),
    // which prevents MapInner's marker effect from tearing down and recreating markers on every render.
    const displayedProjects = useMemo(
        () => projects.filter(p =>
            p.id != null && selectedIds.has(p.id) && p.id !== panelExpandedId
        ),
        [projects, selectedIds, panelExpandedId],
    );

    // Filter projects/scenes shown in the sidebar by search query (case-insensitive substring)
    const searchQuery = panelSearch.trim().toLowerCase();
    const filteredProjects = useMemo(() =>
        searchQuery
            ? projects.filter(p => (p.name ?? '').toLowerCase().includes(searchQuery))
            : projects,
        [projects, searchQuery],
    );
    const filteredIndependentScenes = useMemo(() =>
        searchQuery
            ? independentScenes.filter(s => (s.name ?? '').toLowerCase().includes(searchQuery))
            : independentScenes,
        [independentScenes, searchQuery],
    );
    // When searching, auto-show the independent scenes section if any scenes match
    const showIndependentScenesRow = searchQuery
        ? filteredIndependentScenes.length > 0
        : independentScenes.length > 0;

    // Scene markers to show on map:
    // - scenes from the currently expanded project, plus
    // - independent scenes if the virtual "Independent Scenes" row is selected
    const displayedSceneMarkers = useMemo(() => {
        const markers: SceneResponseDto[] = [];
        if (panelExpandedId && panelExpandedId !== INDEPENDENT_SCENES_ID) {
            markers.push(...panelScenes);
        }
        if (selectedIds.has(INDEPENDENT_SCENES_ID)) {
            markers.push(...independentScenes);
        }
        return markers;
    }, [panelExpandedId, panelScenes, selectedIds, independentScenes]);

    // Currently expanded project (cached to avoid repeated O(n) finds in render)
    const expandedProject = useMemo(
        () => panelExpandedId ? projects.find(p => p.id === panelExpandedId) ?? null : null,
        [panelExpandedId, projects],
    );

    // Location of the currently expanded project (used to pick nearest scene accessor)
    const expandedProjectLocation = useMemo<{ lat: number; lng: number } | null>(() => {
        const acc = expandedProject?.projectAccessors?.[0];
        if (acc?.latitude != null && acc?.longitude != null) {
            return { lat: acc.latitude, lng: acc.longitude };
        }
        return null;
    }, [expandedProject]);

    // Share popover state
    const [sharePopoverOpen, setSharePopoverOpen] = useState(false);
    const [shareCopied, setShareCopied] = useState(false);
    const shareCopiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Close share popover on outside click
    useEffect(() => {
        if (!sharePopoverOpen) return;
        const onClick = (e: MouseEvent) => {
            if (sharePopoverRef.current && !sharePopoverRef.current.contains(e.target as Node)) {
                setSharePopoverOpen(false);
            }
        };
        // Defer one tick so the click that opened the popover doesn't immediately close it
        const id = setTimeout(() => document.addEventListener('mousedown', onClick), 0);
        return () => {
            clearTimeout(id);
            document.removeEventListener('mousedown', onClick);
        };
    }, [sharePopoverOpen]);

    // Handle scene marker tap on map or scene row tap in panel → show detail popover
    const expandedProjectLocationRef = useRef(expandedProjectLocation);
    expandedProjectLocationRef.current = expandedProjectLocation;

    const handleSceneSelect = useCallback((scene: SceneResponseDto) => {
        setSelectedScene(scene);
        setSelectedProject(null);
        // Center map on the scene accessor nearest the project
        const acc = nearestAccessor(scene.sceneAccessors, expandedProjectLocationRef.current);
        if (acc?.latitude != null && acc?.longitude != null) {
            mapInnerRef.current?.panTo(acc.latitude, acc.longitude, 15);
        }
        // Auto-collapse panel on mobile so the map + popover are visible
        if (isMobileRef.current) setPanelOpen(false);
    }, []);

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    if (!GOOGLE_MAPS_API_KEY) {
        return (
            <div className="map-status map-status--error">
                Google Maps API key not configured.
                Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in your environment.
            </div>
        );
    }

    if (!isCurated && geoLoading) {
        return (
            <div className="map-status">
                <div className="loading-spinner" />
                <span>Getting location…</span>
            </div>
        );
    }

    // In curated mode, center on the first project's location (or default)
    const curatedCenter = (() => {
        if (!isCurated) return null;
        for (const p of curatedProjects) {
            const acc = p.projectAccessors?.[0];
            if (acc?.latitude != null && acc?.longitude != null) {
                return { lat: acc.latitude, lng: acc.longitude };
            }
        }
        return null;
    })();

    const lat = curatedCenter?.lat ?? userLat ?? DEFAULT_LAT;
    const lng = curatedCenter?.lng ?? userLng ?? DEFAULT_LNG;

    // Check if the selected project is in the user's nearby list (Experience tab)
    // In curated mode, all projects are openable — skip distance check
    const selectedProjectTooFar = !isCurated && selectedProject != null
        && (selectedProject.id == null || !nearbyProjectIds.has(selectedProject.id));

    return (
        <div className="map-view">
            {geoError && (
                <div className="map-geo-banner">
                    {geoError} — Tap the lock icon in the address bar to re-enable location access.
                </div>
            )}
            {fetchLoading && (
                <div className="map-fetch-indicator">
                    <div className="loading-spinner" style={{ width: 16, height: 16 }} />
                </div>
            )}
            {fetchError && (
                <div className="map-fetch-error" role="alert">
                    {fetchError}
                    <button className="map-select-error__dismiss" onClick={() => setFetchError(null)}>✕</button>
                </div>
            )}
            {selectError && (
                <div className="map-select-error" role="alert">
                    {selectError}
                    <button className="map-select-error__dismiss" onClick={() => setSelectError(null)}>✕</button>
                </div>
            )}
            {/* Project focus header — shown when a project is expanded in the panel */}
            {panelExpandedId && (
                <div className={`map-focus-header ${panelOpen ? 'map-focus-header--offset' : ''}`}>
                    <div className="map-focus-header__pill">
                        <button className="map-focus-header__close" onClick={handleCollapseProject}>
                            ✕
                        </button>
                        <span className="map-focus-header__name">
                            {panelExpandedId === INDEPENDENT_SCENES_ID ? 'Independent Scenes' : (expandedProject?.name ?? 'Project')}
                        </span>
                    </div>
                </div>
            )}

            {/* Popover area — offset by sidebar width on desktop */}
            <div className={`map-popover-area ${panelOpen ? 'map-popover-area--offset' : ''}`}>
                {selectedProject && (
                    <div className="map-project-card">
                        <button className="map-project-card__close" onClick={handleCollapseProject}>✕</button>
                        <div className="map-project-card__body">
                            {(selectedProject.graphicUrl || selectedProject.logoUrl) ? (
                                <img
                                    className="map-project-card__image"
                                    src={resolveUrl(selectedProject.graphicUrl || selectedProject.logoUrl)}
                                    alt=""
                                />
                            ) : (
                                <div className="map-project-card__placeholder">
                                    {(selectedProject.name ?? 'P')[0]}
                                </div>
                            )}
                            <div className="map-project-card__info">
                                <div className="map-project-card__name">{selectedProject.name ?? 'Untitled Project'}</div>
                                {selectedProject.description && (
                                    <div className="map-project-card__desc">{selectedProject.description}</div>
                                )}
                            </div>
                        </div>
                        <button
                            className={`map-project-card__open${selectedProjectTooFar ? ' map-project-card__open--disabled' : ''}`}
                            onClick={handleOpenProject}
                            disabled={selectLoading || selectedProjectTooFar}
                        >
                            {selectLoading ? 'Loading…' : selectedProjectTooFar ? 'Too far away' : 'Open'}
                        </button>
                    </div>
                )}

                {selectedScene && (
                    <div className="map-project-card">
                        <button className="map-project-card__close" onClick={() => setSelectedScene(null)}>✕</button>
                        <div className="map-project-card__body">
                            {selectedScene.imageUrl ? (
                                <img
                                    className="map-project-card__image"
                                    src={resolveUrl(selectedScene.imageUrl)}
                                    alt=""
                                />
                            ) : (
                                <div className="map-project-card__placeholder">
                                    {(selectedScene.name ?? 'S')[0]}
                                </div>
                            )}
                            <div className="map-project-card__info">
                                <div className="map-project-card__name">{selectedScene.name ?? 'Untitled Scene'}</div>
                                {selectedScene.description && (
                                    <div className="map-project-card__desc">{selectedScene.description}</div>
                                )}
                            </div>
                        </div>
                        <button
                            className="map-project-card__open"
                            onClick={() => { onSelectScene(selectedScene); setSelectedScene(null); }}
                        >
                            View in AR
                        </button>
                    </div>
                )}
            </div>{/* end popover area */}

            {/* Side panel — project/scene list */}
            <div className={`map-panel ${panelOpen ? 'map-panel--open' : 'map-panel--closed'}`}>
                <button
                    className="map-panel__toggle"
                    onClick={() => setPanelOpen(o => !o)}
                    aria-label={panelOpen ? 'Collapse panel' : 'Expand panel'}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                        {panelOpen
                            ? <path d="M15 18l-6-6 6-6" />
                            : <path d="M9 18l6-6-6-6" />}
                    </svg>
                </button>
                <div className="map-panel__content">
                    <div className="map-panel__header">
                            <button
                                className="map-panel__visibility-toggle"
                                onClick={toggleSelectAll}
                                title={allSelected ? 'Deselect all' : 'Select all'}
                            >
                                <span className={`map-panel__checkbox ${allSelected ? 'map-panel__checkbox--checked' : selectedIds.size > 0 ? 'map-panel__checkbox--partial' : ''}`}>
                                    {allSelected ? (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    ) : selectedIds.size > 0 ? (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                    ) : null}
                                </span>
                            </button>
                            <span>Projects</span>
                        </div>
                        {/* Search filter */}
                        {(projects.length > 0 || independentScenes.length > 0) && (
                            <div className="map-panel__search">
                                <svg className="map-panel__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <input
                                    className="map-panel__search-input"
                                    type="text"
                                    placeholder="Filter..."
                                    value={panelSearch}
                                    onChange={e => setPanelSearch(e.target.value)}
                                />
                                {panelSearch && (
                                    <button className="map-panel__search-clear" onClick={() => setPanelSearch('')} aria-label="Clear search">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        )}
                        {projects.length === 0 && independentScenes.length === 0 && (
                            <div className="map-panel__empty">No projects in this area</div>
                        )}
                        {searchQuery && filteredProjects.length === 0 && filteredIndependentScenes.length === 0 && (
                            <div className="map-panel__empty">No matches</div>
                        )}
                        <div className="map-panel__list">
                            {filteredProjects.map(project => {
                                const isExpanded = panelExpandedId === project.id;
                                const isSelected = project.id ? selectedIds.has(project.id) : false;
                                return (
                                    <div key={project.id} className={`map-panel__project ${!isSelected ? 'map-panel__project--hidden' : ''}`}>
                                        <div className="map-panel__project-row-wrap">
                                            <button
                                                className="map-panel__visibility-toggle"
                                                onClick={e => { e.stopPropagation(); project.id && toggleProjectSelection(project.id); }}
                                                title={isSelected ? 'Hide from map' : 'Show on map'}
                                            >
                                                <span className={`map-panel__checkbox ${isSelected ? 'map-panel__checkbox--checked' : ''}`}>
                                                    {isSelected && (
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    )}
                                                </span>
                                            </button>
                                            <button
                                                className={`map-panel__project-row map-panel__project-row--with-checkbox ${isExpanded ? 'map-panel__project-row--expanded' : ''}`}
                                                onClick={() => togglePanelProject(project)}
                                            >
                                                {(project.graphicUrl || project.logoUrl) ? (
                                                    <img
                                                        className="map-panel__project-thumb"
                                                        src={resolveUrl(project.graphicUrl || project.logoUrl)}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <div className="map-panel__project-thumb map-panel__project-thumb--placeholder">
                                                        {(project.name ?? 'P')[0]}
                                                    </div>
                                                )}
                                                <span className="map-panel__project-name">{project.name ?? 'Untitled Project'}</span>
                                                <svg className={`map-panel__chevron ${isExpanded ? 'map-panel__chevron--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                                    <path d="M6 9l6 6 6-6" />
                                                </svg>
                                            </button>
                                        </div>
                                        {isExpanded && (
                                            <div className="map-panel__scenes">
                                                {panelScenesLoading && (
                                                    <div className="map-panel__scene-loading">
                                                        <div className="loading-spinner" />
                                                    </div>
                                                )}
                                                {!panelScenesLoading && panelScenesError && (
                                                    <button
                                                        className="map-panel__scene-empty map-panel__scene-retry"
                                                        onClick={() => retryFetchScenes(project)}
                                                    >
                                                        Failed to load — tap to retry
                                                    </button>
                                                )}
                                                {!panelScenesLoading && !panelScenesError && panelScenes.length === 0 && (
                                                    <div className="map-panel__scene-empty">No scenes</div>
                                                )}
                                                {panelScenes.map(scene => (
                                                    <button
                                                        key={scene.id}
                                                        className="map-panel__scene-row"
                                                        onClick={() => handleSceneSelect(scene)}
                                                    >
                                                        {scene.imageUrl ? (
                                                            <img
                                                                className="map-panel__scene-thumb"
                                                                src={resolveUrl(scene.imageUrl)}
                                                                alt=""
                                                            />
                                                        ) : (
                                                            <div className="map-panel__scene-thumb map-panel__scene-thumb--placeholder">
                                                                {(scene.name ?? 'S')[0]}
                                                            </div>
                                                        )}
                                                        <span className="map-panel__scene-name">{scene.name ?? 'Untitled Scene'}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {/* Virtual "Independent Scenes" project */}
                            {showIndependentScenesRow && (
                                <div className={`map-panel__project ${!selectedIds.has(INDEPENDENT_SCENES_ID) ? 'map-panel__project--hidden' : ''}`}>
                                    <div className="map-panel__project-row-wrap">
                                        <button
                                            className="map-panel__visibility-toggle"
                                            onClick={e => { e.stopPropagation(); toggleProjectSelection(INDEPENDENT_SCENES_ID); }}
                                            title={selectedIds.has(INDEPENDENT_SCENES_ID) ? 'Hide from map' : 'Show on map'}
                                        >
                                            <span className={`map-panel__checkbox ${selectedIds.has(INDEPENDENT_SCENES_ID) ? 'map-panel__checkbox--checked' : ''}`}>
                                                {selectedIds.has(INDEPENDENT_SCENES_ID) && (
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                )}
                                            </span>
                                        </button>
                                        <button
                                            className={`map-panel__project-row map-panel__project-row--with-checkbox ${panelExpandedId === INDEPENDENT_SCENES_ID ? 'map-panel__project-row--expanded' : ''}`}
                                            onClick={() => {
                                                if (panelExpandedId === INDEPENDENT_SCENES_ID) {
                                                    handleCollapseProject();
                                                } else {
                                                    ensureSelected(INDEPENDENT_SCENES_ID);
                                                    setSelectedProject(null);
                                                    setSelectedScene(null);
                                                    setPanelExpandedId(INDEPENDENT_SCENES_ID);
                                                    setPanelScenes([]);
                                                    setPanelScenesError(false);
                                                }
                                            }}
                                        >
                                                <div className="map-panel__project-thumb map-panel__project-thumb--placeholder">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                                                        <rect x="3" y="3" width="7" height="7" rx="1.5" />
                                                        <rect x="14" y="3" width="7" height="7" rx="1.5" />
                                                        <rect x="3" y="14" width="7" height="7" rx="1.5" />
                                                        <rect x="14" y="14" width="7" height="7" rx="1.5" />
                                                    </svg>
                                                </div>
                                                <span className="map-panel__project-name">Independent Scenes</span>
                                                <span className="map-panel__project-count">{filteredIndependentScenes.length}</span>
                                            <svg className={`map-panel__chevron ${panelExpandedId === INDEPENDENT_SCENES_ID ? 'map-panel__chevron--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                                <path d="M6 9l6 6 6-6" />
                                            </svg>
                                        </button>
                                    </div>
                                    {panelExpandedId === INDEPENDENT_SCENES_ID && (
                                        <div className="map-panel__scenes">
                                            {filteredIndependentScenes.length === 0 && (
                                                <div className="map-panel__scene-empty">No scenes</div>
                                            )}
                                            {filteredIndependentScenes.map(scene => (
                                                <button
                                                    key={scene.id}
                                                    className="map-panel__scene-row"
                                                    onClick={() => handleSceneSelect(scene)}
                                                >
                                                    {scene.imageUrl ? (
                                                        <img
                                                            className="map-panel__scene-thumb"
                                                            src={resolveUrl(scene.imageUrl)}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <div className="map-panel__scene-thumb map-panel__scene-thumb--placeholder">
                                                            {(scene.name ?? 'S')[0]}
                                                        </div>
                                                    )}
                                                    <span className="map-panel__scene-name">{scene.name ?? 'Untitled Scene'}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* Share footer */}
                        <div className="map-panel__footer">
                            <span className="map-panel__footer-count">{projects.filter(p => p.id && selectedIds.has(p.id)).length} of {projects.length}</span>
                            <div className="map-panel__share-wrap" ref={sharePopoverRef}>
                                <button
                                    className="map-panel__share-btn"
                                    onClick={() => setSharePopoverOpen(o => !o)}
                                    disabled={selectedProjects.length === 0}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" />
                                    </svg>
                                    Share
                                </button>
                                {sharePopoverOpen && (
                                    <div className="map-panel__share-popover">
                                        <button className="map-panel__share-option" onClick={handleCopyLink}>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                            </svg>
                                            {shareCopied ? 'Copied!' : 'Copy Link'}
                                        </button>
                                        <button className="map-panel__share-option" onClick={handleExportKml}>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                                            </svg>
                                            Export KML
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                </div>
            </div>

            <Wrapper
                apiKey={GOOGLE_MAPS_API_KEY}
                libraries={['marker']}
                render={status => status === Status.SUCCESS ? <></> : <MapStatus status={status} />}
            >
                <MapInner
                    ref={mapInnerRef}
                    userLat={lat}
                    userLng={lng}
                    projects={displayedProjects}
                    sceneMarkers={displayedSceneMarkers}
                    sceneMarkerRef={expandedProjectLocation}
                    onSelectProject={handleMarkerTap}
                    onSelectSceneMarker={handleSceneSelect}
                    onCenterChanged={handleCenterChanged}
                    searchCenter={searchCenter}
                    hideGeoOverlays={isCurated}
                />
            </Wrapper>
        </div>
    );
}
